import { Link } from "react-router-dom";
import { Phone, Check, Clock, ArrowRight } from "lucide-react";
import { SEO, localBusinessSchema } from "@/components/SEO";
import { Reveal, RevealGroup, RevealItem } from "@/components/Reveal";
import { BUSINESS } from "@/data/business";
import { SERVICES } from "@/data/services";

export const ServicePageTemplate = ({ service }) => {
  const otherServices = SERVICES.filter((s) => s.slug !== service.slug);

  return (
    <>
      <SEO
        title={service.metaTitle}
        description={service.metaDescription}
        path={service.path}
        schema={localBusinessSchema()}
      />

      {/* HERO */}
      <section
        data-testid={`service-hero-${service.slug}`}
        className="relative h-[70vh] min-h-[480px] flex items-end overflow-hidden"
      >
        <img
          src={service.heroImage}
          alt={`${service.name} ambient treatment setup at The Soul Skin Spa & Salon`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ss-bg via-ss-bg/60 to-ss-bg/20" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-20 w-full">
          <Reveal>
            <p className="text-xs sm:text-sm uppercase tracking-[0.3em] text-ss-gold mb-5">
              The Soul Skin Spa & Salon
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-ss-text max-w-2xl">
              {service.name}
            </h1>
            <p className="mt-4 text-ss-textMuted max-w-lg text-base sm:text-lg">
              {service.tagline}
            </p>
          </Reveal>
        </div>
      </section>

      {/* OVERVIEW + BENEFITS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28 grid grid-cols-1 lg:grid-cols-5 gap-12">
        <Reveal className="lg:col-span-3">
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
            Overview
          </p>
          <p className="font-heading text-2xl sm:text-3xl font-semibold text-ss-text leading-snug">
            {service.description}
          </p>
        </Reveal>

        <Reveal className="lg:col-span-2" delay={0.1}>
          <div className="bg-ss-surface border border-ss-gold/10 rounded-2xl p-7 sm:p-8">
            <p className="text-xs uppercase tracking-[0.25em] text-ss-gold mb-5">
              Key Benefits
            </p>
            <ul className="space-y-3.5">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-ss-text">
                  <Check className="w-4 h-4 mt-1 text-ss-terracotta shrink-0" />
                  <span className="text-sm sm:text-base leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      {/* OILS MENU (if present) */}
      {service.oilsMenu && (
        <section className="bg-ss-bgAlt border-y border-ss-gold/10 py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-ss-gold mb-4">
                Essential Oil Menu
              </p>
            </Reveal>
            <RevealGroup className="flex flex-wrap gap-4 mt-4">
              {service.oilsMenu.map((oil) => (
                <RevealItem key={oil}>
                  <span
                    data-testid={`oil-${oil.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-block bg-ss-surface border border-ss-gold/20 rounded-full px-6 py-3 text-ss-text font-heading text-lg"
                  >
                    {oil}
                  </span>
                </RevealItem>
              ))}
            </RevealGroup>
          </div>
        </section>
      )}

      {/* SESSION PROCESS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
            Your Session
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ss-text max-w-xl">
            What to expect
          </h2>
        </Reveal>

        <RevealGroup className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {service.process.map((step, i) => (
            <RevealItem key={step.title}>
              <div
                data-testid={`process-step-${i}`}
                className="bg-ss-surface border border-ss-gold/10 rounded-2xl p-7 h-full"
              >
                <span className="font-heading text-3xl font-bold text-ss-gold">
                  0{i + 1}
                </span>
                <h3 className="font-heading text-lg font-semibold text-ss-text mt-4">
                  {step.title}
                </h3>
                <p className="text-sm text-ss-textMuted mt-2 leading-relaxed">
                  {step.text}
                </p>
              </div>
            </RevealItem>
          ))}
        </RevealGroup>
      </section>

      {/* DURATIONS + CTA */}
      <section className="bg-ss-bgAlt border-y border-ss-gold/10 py-20 sm:py-28">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-ss-gold mb-4">
              Session Duration
            </p>
            <div
              className="flex flex-wrap justify-center gap-4 mt-2"
              data-testid="duration-options"
            >
              {service.durations.map((d) => (
                <span
                  key={d}
                  className="flex items-center gap-2 border border-ss-gold/25 text-ss-text px-6 py-3 rounded-full text-sm font-medium"
                >
                  <Clock className="w-3.5 h-3.5" /> {d}
                </span>
              ))}
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ss-text mt-12">
              Book this therapy
            </h2>
            <p className="text-ss-textMuted mt-3 max-w-md mx-auto">
              Call us directly for current pricing & availability — no forms
              needed.
            </p>
            <a
              href={`tel:${BUSINESS.phoneClean}`}
              data-testid="service-cta-call-button"
              className="inline-flex items-center gap-2.5 bg-ss-gold text-ss-bg px-8 py-4 rounded-full text-sm font-semibold mt-8 hover:bg-ss-goldHover transition-all duration-300"
            >
              <Phone className="w-4 h-4" /> {BUSINESS.phoneDisplay}
            </a>
          </Reveal>
        </div>
      </section>

      {/* OTHER SERVICES */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-28">
        <Reveal>
          <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-ss-gold mb-4">
            Explore More
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-ss-text max-w-xl">
            Other rituals you may love
          </h2>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
          {otherServices.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.08}>
              <Link
                to={s.path}
                data-testid={`related-service-${s.slug}`}
                className="group relative block rounded-2xl overflow-hidden aspect-[4/5] border border-ss-gold/10"
              >
                <img
                  src={s.cardImage}
                  alt={`${s.name} at The Soul Skin Spa & Salon`}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ss-bg via-ss-bg/20 to-transparent" />
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  <h3 className="font-heading text-xl font-bold text-ss-text">
                    {s.name}
                  </h3>
                  <span className="mt-2 inline-flex items-center gap-1.5 text-ss-gold text-sm group-hover:gap-3 transition-all duration-300">
                    Learn More <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>
    </>
  );
};
